import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../Components/SEO";
import { useGoogleLogin } from "@react-oauth/google";
import { doc, updateDoc } from "firebase/firestore";
import { ArrowRight, Clock, Loader2, Play, Sparkles, Youtube } from "lucide-react";
import TheNav from "../Components/navbar";
import CreatorProfile from "../Components/creatorProfileScreen";
import { useAuth } from "../Context/AuthContext";
import { db } from "../Context/firebaseConfig";
import useVideos from "../hooks/useVideos";
import {
  queueTransactionalEmail,
  transactionalEmails,
} from "../utils/transactionalEmail";

const MINT = "#10b981";
const PURPLE = "#a855f7";

function isYouTubeAuthError(message = "") {
  const text = message.toLowerCase();
  return (
    text.includes("invalid authentication credentials") ||
    text.includes("oauth") ||
    text.includes("access token") ||
    text.includes("unauthorized") ||
    text.includes("401")
  );
}

function VideoCard({ video }) {
  const videoId = typeof video?.id === "string" ? video.id : video?.id?.videoId;
  const thumbnail =
    video?.snippet?.thumbnails?.high?.url ||
    video?.snippet?.thumbnails?.medium?.url ||
    video?.snippet?.thumbnails?.default?.url;

  if (!videoId || !thumbnail) return null;

  return (
    <a
      href={`https://www.youtube.com/watch?v=${videoId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] transition hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.05]"
      style={{ boxShadow: "0 20px 60px -36px rgba(16,185,129,0.65)" }}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={thumbnail}
          alt={video?.snippet?.title || "Creator video"}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07060d] via-black/20 to-transparent" />
        <div className="absolute inset-0 grid place-items-center">
          <span
            className="grid h-12 w-12 place-items-center rounded-full text-black transition group-hover:scale-105"
            style={{ background: MINT, boxShadow: `0 0 30px ${MINT}80` }}
          >
            <Play size={20} fill="currentColor" />
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white">
          {video?.snippet?.title || "Untitled video"}
        </h3>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-white/45">
          <span>{video?.statistics?.viewCount || "N/A"} views</span>
          {video?.snippet?.publishedAt && (
            <>
              <span>•</span>
              <span>{new Date(video.snippet.publishedAt).toLocaleDateString()}</span>
            </>
          )}
        </div>
      </div>
    </a>
  );
}

function YouTubeConnectCard({ error, onConnect }) {
  const isReconnect = Boolean(error);

  return (
    <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
      <div
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-center backdrop-blur-xl sm:p-8"
        style={{ boxShadow: `0 20px 70px -40px ${PURPLE}` }}
      >
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full opacity-25 blur-3xl"
          style={{ background: PURPLE }}
        />
        <div className="relative mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/5">
          <Youtube size={24} style={{ color: MINT }} />
        </div>
        <h2 className="relative mt-4 text-xl font-bold text-white">
          {isReconnect ? "Reconnect your YouTube channel" : "Connect your YouTube channel"}
        </h2>
        <p className="relative mx-auto mt-2 max-w-xl text-sm leading-relaxed text-white/55">
          Editors can understand your content style faster when your best videos are
          visible on your profile.
        </p>
        {error && (
          <p className="relative mx-auto mt-4 max-w-xl rounded-2xl border border-red-400/20 bg-red-500/10 p-3 text-sm text-red-100">
            {error}
          </p>
        )}
        <button
          type="button"
          onClick={onConnect}
          className="relative mt-5 inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold uppercase tracking-wider text-black transition active:scale-[0.98]"
          style={{
            background: `linear-gradient(135deg, ${MINT}, #34d399)`,
            boxShadow: `0 16px 44px -16px ${MINT}`,
          }}
        >
          <Youtube size={16} />
          {isReconnect ? "Reconnect or switch account" : "Connect YouTube"}
        </button>
      </div>
    </section>
  );
}

function YouTubeStatusCard() {
  return (
    <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center text-white/55 backdrop-blur-xl">
        <Loader2 className="mx-auto mb-3 animate-spin text-white/35" size={24} />
        Checking your YouTube connection...
      </div>
    </section>
  );
}

function CreatorToolsCta() {
  return (
    <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6">
      <div className="flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40">
            <Sparkles size={13} style={{ color: MINT }} />
            Private workflow
          </div>
          <h2 className="mt-2 text-xl font-bold text-white">Creator Tools</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/55">
            Save editing instruction templates, organize projects, and plan batches
            without changing your public profile.
          </p>
        </div>
        <Link
          to="/creator-tools"
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-emerald-400 px-4 text-sm font-bold text-black transition hover:bg-emerald-300 active:scale-[0.98]"
        >
          Open Creator Tools
          <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

function TopVideosSection({ videos, loading, error, onSwitchAccount }) {
  return (
    <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40">
            <Sparkles size={13} style={{ color: MINT }} />
            Connected channel
          </div>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Top Videos
          </h2>
          <p className="mt-1 text-sm text-white/55">
            A quick snapshot of your content style for editors.
          </p>
        </div>
        <button
          type="button"
          onClick={onSwitchAccount}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white/75 transition hover:bg-white/[0.07] hover:text-white"
        >
          <Youtube size={14} />
          Switch YouTube account
        </button>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="h-64 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]"
            />
          ))}
        </div>
      ) : error ? (
        <div
          className="relative overflow-hidden rounded-3xl border border-red-400/20 bg-red-500/10 p-6 text-center backdrop-blur-xl sm:p-8"
        >
          <h3 className="text-lg font-bold text-red-100">
            We could not load your YouTube videos
          </h3>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-red-100/75">
            Your YouTube connection expired. Reconnect or switch accounts to
            refresh your portfolio videos.
          </p>
          <button
            type="button"
            onClick={onSwitchAccount}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold uppercase tracking-wider text-black transition active:scale-[0.98]"
            style={{
              background: `linear-gradient(135deg, ${MINT}, #34d399)`,
              boxShadow: `0 16px 44px -16px ${MINT}`,
            }}
          >
            <Youtube size={16} />
            Switch YouTube account
          </button>
        </div>
      ) : videos.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {videos.map((video, index) => (
            <VideoCard key={video?.id?.videoId || video?.id || index} video={video} />
          ))}
        </div>
      ) : (
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center text-white/55 backdrop-blur-xl">
          <Clock className="mx-auto mb-3 text-white/35" size={24} />
          No videos found yet.
        </div>
      )}
    </section>
  );
}

export default function CreatorPortfolio() {
  const { currentUser, accessToken, setAccessToken, youtubeTokenLoading } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchedTokenRef = useRef(null);
  const { fetchVideos } = useVideos();

  const clearStoredYouTubeConnection = async () => {
    lastFetchedTokenRef.current = null;
    setAccessToken(null);
    setVideos([]);
    localStorage.removeItem("videos");
    localStorage.removeItem("videos_timestamp");

    if (currentUser?.uid) {
      await updateDoc(doc(db, "Users", currentUser.uid), {
        youtubeAccessToken: "",
      });
    }
  };

  useEffect(() => {
    if (!currentUser || !accessToken) return;
    if (lastFetchedTokenRef.current === accessToken) return;

    lastFetchedTokenRef.current = accessToken;
    setLoading(true);
    fetchVideos(accessToken)
      .then((response) => {
        setVideos(response || []);
        setError(null);
      })
      .catch((err) => {
        if (isYouTubeAuthError(err.message)) {
          clearStoredYouTubeConnection().catch((clearError) => {
            console.error("Failed to clear expired YouTube token:", clearError);
          });
          setError("Your YouTube connection expired. Please reconnect your account.");
          return;
        }

        console.error("Failed to load YouTube videos:", err);
        setError("We could not load your YouTube videos. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [accessToken, currentUser]);

  const handleGoogleLogin = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/youtube.readonly",
    prompt: "select_account consent",
    onSuccess: async (response) => {
      setAccessToken(response.access_token);
      lastFetchedTokenRef.current = null;
      localStorage.removeItem("videos");
      localStorage.removeItem("videos_timestamp");
      await updateDoc(doc(db, "Users", currentUser.uid), {
        youtubeAccessToken: response.access_token,
      });
      await queueTransactionalEmail({
        to: currentUser.email,
        email: transactionalEmails.youtubeConnected(),
      });
      if (response.access_token) {
        const freshVideos = await fetchVideos(response.access_token, true);
        setVideos(freshVideos || []);
        setError(null);
      }
    },
    onFailure: (err) => {
      console.error("Google login failed:", err);
      setError("We could not connect your YouTube account. Please try again.");
    },
  });

  return (
    <div className="min-h-screen bg-[#07060d] text-white">
      <SEO
        title="Creator Portfolio Workspace | Kre8"
        description="Private Kre8 creator portfolio workspace."
        canonicalPath="/creatorPortfolio"
        noindex
      />
      <TheNav />
      <main className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[780px] -translate-x-1/2 rounded-full opacity-20 blur-[130px]"
          style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 60%)` }}
        />
        <div
          className="pointer-events-none absolute right-0 top-80 h-[380px] w-[520px] rounded-full opacity-16 blur-[120px]"
          style={{ background: `radial-gradient(circle, ${MINT}, transparent 60%)` }}
        />

        <CreatorProfile />
        <CreatorToolsCta />

        {youtubeTokenLoading ? (
          <YouTubeStatusCard />
        ) : !accessToken ? (
          <YouTubeConnectCard error={error} onConnect={handleGoogleLogin} />
        ) : (
          <TopVideosSection
            videos={videos}
            loading={loading}
            error={error}
            onSwitchAccount={handleGoogleLogin}
          />
        )}
      </main>
    </div>
  );
}
